import { FormulaTokenTipoEnum } from '@shared/enums/formula/formula-token-tipo.enum';
import { FormulaExpressaoToken } from './formula-expressao-token';
import { FormulaExpressaoValidacao } from './formula-expressao-validacao';
import { FormulaExpressaoValidationError } from './formula-expressao-validation-error';

export { FormulaExpressaoToken } from './formula-expressao-token';
export { FormulaExpressaoValidacao } from './formula-expressao-validacao';

const TOKEN_REGEX = /\{[^}]*\}|#[^#]*#|@[A-Za-z]+|>=|<=|<>|[+\-*/()><=,]|[A-Za-z]+/g;
const CHAVE_VARIAVEL_REGEX = /^[a-z0-9_][a-z0-9_.]*$/;
const CONSTANTE_REGEX = /^\d+(?:\.\d+)?$/;
const OPERADORES = ['+', '-', '*', '/', '>', '<', '>=', '<=', '=', '<>', 'e', 'ou'];
const OPERADORES_ARITMETICOS = ['+', '-', '*', '/'];
const OPERADORES_COMPARACAO = ['>', '<', '>=', '<=', '=', '<>'];
const OPERADORES_LOGICOS = ['e', 'ou'];
const OPERADORES_COMPARACAO_LOGICOS = [...OPERADORES_COMPARACAO, ...OPERADORES_LOGICOS];
const FUNCOES_PERMITIDAS = ['@se', '@soma', '@rateio'];

export function tokenizarExpressaoFormula(expressao: string): FormulaExpressaoToken[] {
    try {
        return tokenizarOuFalhar(expressao);
    } catch {
        return [];
    }
}

export function serializarTokensExpressao(tokens: FormulaExpressaoToken[]): string {
    let resultado = '';
    for (const token of tokens) {
        if (token.tipo === FormulaTokenTipoEnum.Operador) {
            resultado = resultado.trimEnd() + ` ${token.valor} `;
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Separador) {
            resultado = resultado.trimEnd() + ', ';
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Parentese && token.valor === ')') {
            resultado = resultado.trimEnd() + ')';
            continue;
        }

        resultado += token.valor;
    }
    return resultado.trim();
}

export function validarExpressaoFormula(
    expressao: string,
    variaveisDisponiveis: string[],
    variaveisBooleanas: string[] = [],
): FormulaExpressaoValidacao {
    const erros: NonNullable<FormulaExpressaoValidacao['erros']> = [];

    if (expressao?.includes(';')) {
        adicionarErro(erros, new FormulaExpressaoValidationError(
            "O separador ';' não é permitido. Use vírgula para separar os argumentos das funções.",
            0,
        ));
    }

    if (/#\s*\d+\s*,\s*\d+\s*#/.test(expressao ?? '')) {
        adicionarErro(erros, new Error('Constante numérica deve usar ponto como separador decimal (ex.: #12.58#), não vírgula.'));
    }

    let tokens: FormulaExpressaoToken[];
    try {
        tokens = tokenizarOuFalhar(expressao);
    } catch (err) {
        adicionarErro(erros, err);
        return resultadoValidacao(erros);
    }

    if (tokens.length === 0) {
        adicionarErro(erros, new Error('Informe uma expressão para a fórmula personalizada.'));
        return resultadoValidacao(erros);
    }

    const chavesPermitidas = new Set(variaveisDisponiveis.filter(Boolean).map(v => v.toLowerCase()));
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.tipo !== FormulaTokenTipoEnum.Variavel) continue;
        const chave = token.valor.slice(1, -1).toLowerCase();
        if (!chavesPermitidas.has(chave)) {
            adicionarErro(erros, new FormulaExpressaoValidationError(`A variável ${token.valor} não está disponível para esta fórmula.`, i));
        }
    }

    coletarErroValidacao(erros, () => validarParenteses(tokens));
    coletarErroValidacao(erros, () => validarSeparadores(tokens));
    const chavesBooleanas = new Set(variaveisBooleanas.filter(Boolean).map(v => v.toLowerCase()));
    coletarErroValidacao(erros, () => validarCondicaoSe(tokens, chavesBooleanas));
    validarSequencia(tokens).forEach(erro => adicionarErro(erros, erro));

    return resultadoValidacao(erros);
}

function coletarErroValidacao(erros: NonNullable<FormulaExpressaoValidacao['erros']>, validar: () => void): void {
    try {
        validar();
    } catch (err) {
        adicionarErro(erros, err);
    }
}

function adicionarErro(erros: NonNullable<FormulaExpressaoValidacao['erros']>, err: unknown): void {
    const erro = {
        mensagem: err instanceof Error ? err.message : 'Expressão inválida.',
        tokenIndex: err instanceof FormulaExpressaoValidationError ? err.tokenIndex : undefined,
    };
    if (!erros.some(e => e.mensagem === erro.mensagem && e.tokenIndex === erro.tokenIndex)) {
        erros.push(erro);
    }
}

function resultadoValidacao(erros: NonNullable<FormulaExpressaoValidacao['erros']>): FormulaExpressaoValidacao {
    if (erros.length === 0) return { valida: true };
    return {
        valida: false,
        mensagem: erros[0].mensagem,
        tokenIndex: erros[0].tokenIndex,
        erros,
    };
}

function tokenizarOuFalhar(expressao: string): FormulaExpressaoToken[] {
    if (!expressao?.trim()) return [];

    const tokens: FormulaExpressaoToken[] = [];
    const partes = expressao.match(TOKEN_REGEX) ?? [];
    for (let i = 0; i < partes.length; i++) {
        try {
            tokens.push(classificarToken(partes[i]));
        } catch (err) {
            throw new FormulaExpressaoValidationError(
                err instanceof Error ? err.message : 'Expressão inválida.',
                i,
            );
        }
    }
    return tokens;
}

function classificarToken(valor: string): FormulaExpressaoToken {
    if (valor.startsWith('{') && valor.endsWith('}')) {
        const chave = valor.slice(1, -1);
        if (!CHAVE_VARIAVEL_REGEX.test(chave)) {
            throw new Error(`A chave da variável ${valor} é inválida. Use letras minúsculas, números, ponto e underline.`);
        }
        return { tipo: FormulaTokenTipoEnum.Variavel, valor, display: valor };
    }

    if (valor.startsWith('#') && valor.endsWith('#')) {
        const numero = valor.slice(1, -1);
        if (!CONSTANTE_REGEX.test(numero)) {
            throw new Error(`Constante numérica inválida: ${valor}. Use o formato #100# ou #12.58#.`);
        }
        return { tipo: FormulaTokenTipoEnum.Constante, valor, display: formatarConstante(valor) };
    }

    if (valor.startsWith('@')) {
        const funcao = valor.toLowerCase();
        if (!FUNCOES_PERMITIDAS.includes(funcao)) {
            throw new Error(`A função ${valor} não é reconhecida.`);
        }
        return { tipo: FormulaTokenTipoEnum.Funcao, valor: funcao, display: funcao };
    }

    if (valor === '(' || valor === ')') return { tipo: FormulaTokenTipoEnum.Parentese, valor, display: valor };
    if (valor === ',') return { tipo: FormulaTokenTipoEnum.Separador, valor, display: valor };

    const operador = valor.toLowerCase();
    if (OPERADORES.includes(operador)) {
        return { tipo: FormulaTokenTipoEnum.Operador, valor: operador, display: formatarOperador(operador) };
    }

    throw new Error(`Token inválido na expressão: ${valor}.`);
}

function validarParenteses(tokens: FormulaExpressaoToken[]): void {
    const aberturas: number[] = [];
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.tipo !== FormulaTokenTipoEnum.Parentese) continue;
        if (token.valor === '(') {
            aberturas.push(i);
            continue;
        }

        if (aberturas.length === 0) {
            throw new FormulaExpressaoValidationError('A expressão possui parênteses fechando sem abertura correspondente.', i);
        }
        aberturas.pop();
    }
    if (aberturas.length > 0) {
        throw new FormulaExpressaoValidationError('A expressão possui parênteses não fechados.', aberturas[aberturas.length - 1]);
    }
}

function validarSeparadores(tokens: FormulaExpressaoToken[]): void {
    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].tipo !== FormulaTokenTipoEnum.Funcao) continue;
        const funcao = tokens[i].valor;
        if (tokens[i + 1]?.valor !== '(') throw new FormulaExpressaoValidationError(`Use ${funcao} seguido de parênteses.`, i);
        const fim = encontrarFechamento(tokens, i + 1);
        if (fim === -1) throw new FormulaExpressaoValidationError(`A função ${funcao} não foi fechada.`, i);
        const argumentos = contarSeparadoresTopo(tokens, i + 1, fim) + 1;
        if (funcao === '@se' && argumentos !== 3) {
            throw new FormulaExpressaoValidationError('Use o condicional no formato @se(condição, verdadeiro, falso).', i);
        }
        if (funcao === '@soma' && (fim === i + 2 || argumentos < 1))
            throw new FormulaExpressaoValidationError('@soma exige ao menos um argumento.', i);
        if (funcao === '@rateio' && argumentos !== 2)
            throw new FormulaExpressaoValidationError('Use o rateio no formato @rateio(valor, base).', i);
    }

    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].tipo === FormulaTokenTipoEnum.Separador && !estaDentroDeFuncao(tokens, i)) {
            throw new FormulaExpressaoValidationError('A vírgula só pode ser usada para separar argumentos de uma função.', i);
        }
    }
}

function validarCondicaoSe(tokens: FormulaExpressaoToken[], chavesBooleanas: Set<string>): void {
    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i].tipo !== FormulaTokenTipoEnum.Funcao || tokens[i].valor !== '@se') continue;
        if (tokens[i + 1]?.valor !== '(') continue;
        const fim = encontrarFechamento(tokens, i + 1);
        if (fim === -1) continue;

        let nivel = 0;
        let condFim = -1;
        for (let j = i + 2; j < fim; j++) {
            const t = tokens[j];
            if (t.tipo === FormulaTokenTipoEnum.Parentese) nivel += t.valor === '(' ? 1 : -1;
            else if (t.tipo === FormulaTokenTipoEnum.Separador && nivel === 0) { condFim = j; break; }
        }
        if (condFim === -1) continue;

        const tokensCondicao = tokens.slice(i + 2, condFim);
        const temComparacao = tokensCondicao
            .some(t => t.tipo === FormulaTokenTipoEnum.Operador && OPERADORES_COMPARACAO_LOGICOS.includes(t.valor));
        const variavelBooleanaDireta = tokensCondicao.length === 1
            && tokensCondicao[0].tipo === FormulaTokenTipoEnum.Variavel
            && chavesBooleanas.has(tokensCondicao[0].valor.slice(1, -1).toLowerCase());
        if (!temComparacao && !variavelBooleanaDireta) {
            throw new FormulaExpressaoValidationError(
                'A condição do @se deve ser uma variável booleana ou conter uma comparação (>, <, =, etc.).',
                i + 2,
            );
        }
    }
}

function validarSequencia(tokens: FormulaExpressaoToken[]): FormulaExpressaoValidationError[] {
    const erros: FormulaExpressaoValidationError[] = [];
    let esperaOperando = true;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.tipo === FormulaTokenTipoEnum.Separador) {
            if (esperaOperando) erros.push(new FormulaExpressaoValidationError('A função possui um argumento vazio.', i));
            esperaOperando = true;
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Funcao) {
            if (!esperaOperando) erros.push(new FormulaExpressaoValidationError('Há uma função em posição inválida na expressão.', i));
            esperaOperando = true;
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Parentese) {
            if (token.valor === '(') {
                esperaOperando = true;
            } else {
                if (esperaOperando) erros.push(new FormulaExpressaoValidationError('Há um parêntese fechando após operador ou expressão vazia.', i));
                esperaOperando = false;
            }
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Operador) {
            if (esperaOperando) erros.push(new FormulaExpressaoValidationError('Há um operador sem valor antes dele.', i));
            esperaOperando = true;
            continue;
        }

        if (!esperaOperando) erros.push(new FormulaExpressaoValidationError('Há valores consecutivos sem operador entre eles.', i));
        esperaOperando = false;
    }

    const ultimoIndex = tokens.length - 1;
    if (esperaOperando && !erros.some(erro => erro.tokenIndex === ultimoIndex)) {
        erros.push(new FormulaExpressaoValidationError('A expressão não pode terminar com operador.', ultimoIndex));
    }
    return erros;
}

function contarSeparadoresTopo(tokens: FormulaExpressaoToken[], aberturaIndex: number, fechamentoIndex: number): number {
    let nivel = 0;
    let total = 0;
    for (let i = aberturaIndex; i < fechamentoIndex; i++) {
        if (tokens[i].tipo === FormulaTokenTipoEnum.Parentese) nivel += tokens[i].valor === '(' ? 1 : -1;
        else if (tokens[i].tipo === FormulaTokenTipoEnum.Separador && nivel === 1) total++;
    }
    return total;
}

function encontrarFechamento(tokens: FormulaExpressaoToken[], aberturaIndex: number): number {
    let nivel = 0;
    for (let i = aberturaIndex; i < tokens.length; i++) {
        if (tokens[i].tipo !== FormulaTokenTipoEnum.Parentese) continue;
        nivel += tokens[i].valor === '(' ? 1 : -1;
        if (nivel === 0) return i;
    }
    return -1;
}

function estaDentroDeFuncao(tokens: FormulaExpressaoToken[], tokenIndex: number): boolean {
    for (let i = tokenIndex - 1; i >= 0; i--) {
        if (tokens[i].tipo !== FormulaTokenTipoEnum.Funcao) continue;
        if (tokens[i + 1]?.valor !== '(') continue;
        const fim = encontrarFechamento(tokens, i + 1);
        if (fim >= tokenIndex) return true;
    }
    return false;
}

function formatarOperador(operador: string): string {
    switch (operador) {
        case '*': return 'x';
        default: return operador;
    }
}

function formatarConstante(valor: string): string {
    const conteudo = valor.slice(1, -1);
    const numero = Number(conteudo);
    if (!Number.isFinite(numero)) return valor;
    const casasDecimais = conteudo.includes('.') ? Math.max(2, conteudo.split('.')[1].length) : 0;
    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: casasDecimais,
        maximumFractionDigits: casasDecimais,
    });
}
